import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { BidAutomationRules } from '~/components/GoogleAds/BidAutomationRules';
import HoverNav from '~/components/GoogleAds/HoverNav';

import {
  AdGroupDetails,
  BiddingControllerType,
  getAdGroup,
  updateBiddingController,
} from '~/services/PublicOfferFeedService';

const getBiddingStrategyInfo = (overview: AdGroupDetails) => {
  const strategyType = overview?.bidding_strategy_type;

  if (!strategyType) {
    return null;
  }

  const strategyConfigs = {
    TARGET_ROAS: {
      heading: 'Current Target ROAS',
      value: overview?.troas > 0 ? `${overview.troas} %` : null,
    },
    MAXIMIZE_CONVERSION_VALUE: {
      heading: 'CurrentTarget ROAS',
      value: overview?.troas > 0 ? `${overview.troas} %` : null,
    },
    TARGET_CPA: {
      heading: 'Target CPA',
      value: overview?.tcpa > 0 ? overview.tcpa : null,
    },
    MAXIMIZE_CONVERSIONS: {
      heading: 'Target CPA',
      value: overview?.tcpa > 0 ? overview.tcpa : null,
    },
    PERCENT_CPC: {
      heading: 'CPC %',
      value: overview?.tcpc_percentage > 0 ? `${overview.tcpc_percentage} %` : null,
    },
    MANUAL_CPC: {
      heading: 'Target CPC',
      value: overview?.tcpc > 0 ? `$ ${overview.tcpc}` : null,
    },
  };

  return strategyConfigs[strategyType] || null;
};

function AdGroupDetailsView() {
  const { customerId, campaignId, adGroupId } = useParams<{
    customerId: string;
    campaignId: string;
    adGroupId: string;
  }>();

  const [fetchingState, setFetchingState] = useState<Utils.FetchingState>('idle');
  const [overview, setOverview] = useState<AdGroupDetails>(null);
  const [biddingController, setBiddingController] = useState<BiddingControllerType>('matrix');
  const [isBiddingControllerDialogOpen, setIsBiddingControllerDialogOpen] = useState(false);
  const [targetDailyAdSpend, setTargetDailyAdSpend] = useState<string>('');
  const [currentTargetDailyAdSpend, setCurrentTargetDailyAdSpend] = useState<number | null>(null);
  const [isUpdatingBiddingController, setIsUpdatingBiddingController] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const fetchData = useCallback(
    async (customerId: string, campaignId: string, adGroupId: string) => {
      try {
        setFetchingState('loading');
        const { result } = await getAdGroup(customerId, campaignId, adGroupId);
        setOverview(result);

        const rawController =
          (result as AdGroupDetails & { biddingController?: string; bidding_controller?: string })?.biddingController ??
          (result as AdGroupDetails & { bidding_controller?: string })?.bidding_controller;
        const controllerFromResult: BiddingControllerType = rawController === 'pid' ? 'pid' : 'matrix';

        const rawTargetDailyAdSpend =
          (result as AdGroupDetails & { targetDailyAdSpend?: number; target_daily_ad_spend?: number })
            ?.targetDailyAdSpend ??
          (result as AdGroupDetails & { target_daily_ad_spend?: number })?.target_daily_ad_spend;
        const targetDailyAdSpendFromResult = typeof rawTargetDailyAdSpend === 'number' ? rawTargetDailyAdSpend : null;

        setBiddingController(controllerFromResult);
        setCurrentTargetDailyAdSpend(controllerFromResult === 'pid' ? targetDailyAdSpendFromResult : null);

        setFetchingState('success');
      } catch (error) {
        setFetchingState('failed');
        enqueueSnackbar('Failed to fetch data', { variant: 'error' });
      }
    },
    [enqueueSnackbar],
  );

  useEffect(() => {
    fetchData(customerId, campaignId, adGroupId);
  }, [customerId, campaignId, adGroupId, fetchData]);

  const handleOpenPidDialog = () => {
    setTargetDailyAdSpend('');
    setIsBiddingControllerDialogOpen(true);
  };

  const handleClosePidDialog = () => {
    if (isUpdatingBiddingController) {
      return;
    }
    setIsBiddingControllerDialogOpen(false);
  };

  const handleSwitchToPid = async () => {
    const value = Number(targetDailyAdSpend);
    if (Number.isNaN(value) || value <= 0) {
      enqueueSnackbar('Please enter a valid Target Daily Ad Spend', { variant: 'warning' });
      return;
    }

    try {
      setIsUpdatingBiddingController(true);
      const response = await updateBiddingController(adGroupId, {
        controller: 'pid',
        targetDailyAdSpend: value,
      });
      const controllerFromResponse = response?.result?.biddingController === 'pid' ? 'pid' : 'matrix';
      const targetFromResponse =
        typeof response?.result?.targetDailyAdSpend === 'number' ? response.result.targetDailyAdSpend : value;

      setBiddingController(controllerFromResponse);
      setCurrentTargetDailyAdSpend(targetFromResponse);
      enqueueSnackbar('Switched to PID controller', { variant: 'success' });
      setIsBiddingControllerDialogOpen(false);
    } catch (error) {
      enqueueSnackbar('Failed to update bidding controller', { variant: 'error' });
    } finally {
      setIsUpdatingBiddingController(false);
    }
  };

  const handleSwitchToMatrix = async () => {
    try {
      setIsUpdatingBiddingController(true);
      const response = await updateBiddingController(adGroupId, {
        controller: 'matrix',
      });
      const controllerFromResponse = response?.result?.biddingController === 'pid' ? 'pid' : 'matrix';
      setBiddingController(controllerFromResponse);
      setCurrentTargetDailyAdSpend(null);
      enqueueSnackbar('Switched to Matrix controller', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to update bidding controller', { variant: 'error' });
    } finally {
      setIsUpdatingBiddingController(false);
    }
  };

  if (fetchingState === 'loading') {
    return (
      <Box p={2} display="flex" justifyContent="center">
        <CircularProgress variant="indeterminate" />
      </Box>
    );
  }

  return (
    <Box className="offer-deal with-side-bar">
      <Box className="side-bar">
        <HoverNav />
      </Box>
      <Container maxWidth="xl">
        <PageSubheader title={overview?.ad_group_name}>
          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
            {overview?.status}
          </Typography>
        </PageSubheader>

        <Box mt={2} mb={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={biddingController === 'matrix' ? handleOpenPidDialog : handleSwitchToMatrix}
            disabled={isUpdatingBiddingController}
          >
            {biddingController === 'matrix' ? 'Switch to PID method' : 'Switch to Matrix controller'}
          </Button>
        </Box>

        <Paper sx={{ my: 4 }} id="overview" className="branded-panel">
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 8,
                lg: 9,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Ad Group Id
                  </Typography>
                  <Typography fontSize={16}> {overview?.ad_group_id}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Property
                  </Typography>
                  <Typography fontSize={16}>{overview?.hotel}</Typography>
                </Box>

                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Campaign Name
                  </Typography>
                  <Typography fontSize={16}> {overview?.campaign_name}</Typography>
                </Box>
                {overview?.campaign_start_date && (
                  <Box>
                    <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                      Campaign Start Date
                    </Typography>
                    <Typography fontSize={16}> {overview?.campaign_start_date}</Typography>
                  </Box>
                )}
                {overview?.campaign_end_date && (
                  <Box>
                    <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                      Campaign End Date
                    </Typography>
                    <Typography fontSize={16}> {overview?.campaign_end_date}</Typography>
                  </Box>
                )}

                <Box sx={{ mt: 3 }}>
                  <Typography variant="h5" fontSize={18} fontWeight="bold" sx={{ mb: 2 }}>
                    Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 6,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Spend
                          </Typography>
                          <Typography fontSize={16}> ${overview?.spend}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Views
                          </Typography>
                          <Typography fontSize={16}> {overview?.views}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin
                          </Typography>
                          <Typography fontSize={16}> ${overview?.margin}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Clicks
                          </Typography>
                          <Typography fontSize={16}> {overview?.clicks}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Impressions
                          </Typography>
                          <Typography fontSize={16}> {overview?.impressions}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 6,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            CPC
                          </Typography>
                          <Typography fontSize={16}> ${overview?.cpc}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            CTR
                          </Typography>
                          <Typography fontSize={16}> {overview?.ctr}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin per View
                          </Typography>
                          <Typography fontSize={16}> ${overview?.mpv}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Impression Share
                          </Typography>
                          <Typography fontSize={16}> {overview?.impression_share}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin ROAS
                          </Typography>
                          <Typography fontSize={16}> {overview?.mroas}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 4,
                      }}
                    >
                      <Stack direction="column" spacing={2}>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Value
                          </Typography>
                          <Typography fontSize={16}> ${overview?.conversion_value}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversions
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversions}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin/View Change (3d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.margin_per_view_change_3d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Margin/View Change (1d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.margin_per_view_change_1d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Rate Change (3d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversion_rate_change_3d_vs_7d_pct}%</Typography>
                        </Box>
                        <Box>
                          <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                            Conversion Rate Change (1d vs 7d)
                          </Typography>
                          <Typography fontSize={16}> {overview?.conversion_rate_change_1d_vs_7d_pct}%</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </Stack>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 4,
                lg: 3,
              }}
            >
              <Stack direction="column" spacing={2}>
                <Box>
                  <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                    Bidding Strategy
                  </Typography>
                  <Typography fontSize={16}> {overview?.bidding_strategy_type}</Typography>
                </Box>
                {(() => {
                  const strategyInfo = overview ? getBiddingStrategyInfo(overview) : null;
                  if (!strategyInfo?.value) {
                    return null;
                  }
                  return (
                    <Box>
                      <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                        {strategyInfo.heading}
                      </Typography>
                      <Typography fontSize={16}> {strategyInfo.value}</Typography>
                    </Box>
                  );
                })()}
                {biddingController === 'pid' && (
                  <>
                    {currentTargetDailyAdSpend !== null && (
                      <Box>
                        <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                          Target Daily Ad Spend
                        </Typography>
                        <Typography fontSize={16}>${currentTargetDailyAdSpend.toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="h6" fontSize={16} color="secondary" textTransform="uppercase">
                        Bidding Controller
                      </Typography>
                      <Typography fontSize={16}>PID</Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Paper>
        <BidAutomationRules
          campaignId={campaignId}
          adGroupId={adGroupId}
          customerId={customerId}
          fetchData={fetchData}
          biddingController={biddingController}
        />
      </Container>
      <Dialog open={isBiddingControllerDialogOpen} onClose={handleClosePidDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Switch to PID method</DialogTitle>
        <DialogContent>
          <TextField
            label="Target Daily Ad Spend"
            type="number"
            fullWidth
            margin="normal"
            value={targetDailyAdSpend}
            onChange={(event) => setTargetDailyAdSpend(event.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePidDialog} disabled={isUpdatingBiddingController}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSwitchToPid}
            disabled={isUpdatingBiddingController}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdGroupDetailsView;
